import React, { useState, useEffect } from 'react';
import { MessageSquare, Trash2, ImagePlus, Video as VideoIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../hooks/auth/useAuth';
import { memo } from 'react';

function formatDate(dateString) {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
}

export const Post = memo(function Post({ post, onComment, onDelete, onLike }) {
  const [isCommenting, setIsCommenting] = useState(false);
  const [commentContent, setCommentContent] = useState('');
  const [avatarError, setAvatarError] = useState(false);
  const { user } = useAuth();

  const getDisplayName = (profile) => {
    if (!profile) {
      return 'User';
    }
    
    const firstName = profile.first_name || '';
    const lastName = profile.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    
    return fullName || 'User';
  };

  const getAvatarUrl = (profile) => {
    if (!profile) {
      return `https://ui-avatars.com/api/?name=User&background=random`;
    }
    
    if (!profile.avatar_url || avatarError) {
      const name = getDisplayName(profile);
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`;
    }
    
    // Use the versioned URL directly
    return profile.avatar_url;
  };

  const handleSubmitComment = (e) => {
    e.preventDefault();
    if (!commentContent.trim()) return;
    
    onComment(post.id, commentContent);
    setCommentContent('');
    setIsCommenting(false);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await onDelete(post.id);
        toast.success('Post deleted successfully');
      } catch (error) {
        console.error('Error deleting post:', error);
        toast.error('Failed to delete post');
      }
    }
  };

  return (
    <div className="bg-background-card rounded-[8px] p-6 shadow-lg">
      <div className="flex items-start space-x-3 mb-4">
        <img
          src={getAvatarUrl(post.user_profiles)}
          alt={post.user_profiles ? getDisplayName(post.user_profiles) : 'User'}
          className="w-10 h-10 rounded-full object-cover bg-gray-100"
          onError={(e) => {
            setAvatarError(true);
            const target = e.target;
            const name = post.user_profiles ? getDisplayName(post.user_profiles) : 'Anonymous';
            target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`;
          }}
        />
        <div>
          <h3 className="font-medium text-black">
            <div className="flex items-center gap-2"> 
              <span>{getDisplayName(post.user_profiles)}</span>
              {post.user_profiles?.is_admin && (
                <span className="px-2 py-0.5 bg-black text-white text-xs rounded-full">
                  Admin
                </span>
              )}
            </div>
          </h3>
          <p className="text-sm text-black">
            {formatDate(post.created_at)}
          </p>
          {user?.id === post.user_id && (
            <button
              onClick={handleDelete}
              className="mt-2 flex items-center text-red-500 hover:text-red-600 text-sm"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete Post
            </button>
          )}
        </div>
      </div>

      <p className="text-black mb-4">{post.content}</p>

      {/* Media Display */}
      {post.media_url && (
        <div className="mb-4 rounded-lg overflow-hidden">
          {post.media_type === 'image' ? (
            <div className="relative group">
              <img
                src={post.media_url}
                alt="Post attachment"
                className="w-full h-auto max-h-[500px] object-contain bg-gray-100"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="absolute top-4 left-4 bg-black/50 text-white px-2 py-1 rounded-lg text-sm flex items-center gap-1">
                <ImagePlus className="h-4 w-4" />
                <span>Image</span>
              </div>
            </div>
          ) : post.media_type === 'video' ? (
            <div className="relative group">
              <video
                src={post.media_url}
                controls
                className="w-full max-h-[500px]"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              >
                Your browser does not support the video tag.
              </video>
              <div className="absolute top-4 left-4 bg-black/50 text-white px-2 py-1 rounded-lg text-sm flex items-center gap-1">
                <VideoIcon className="h-4 w-4" />
                <span>Video</span>
              </div>
            </div>
          ) : null}
        </div>
      )}

      <div className="flex items-center space-x-6 text-gray-400">
        <button
          onClick={() => setIsCommenting(!isCommenting)}
          className="flex items-center gap-2 hover:text-[#800020] transition-colors"
        >
          <MessageSquare className="h-5 w-5" />
          <span className="text-black">{Array.isArray(post.comments) ? post.comments.length : 0}</span>
        </button>
        
        {onLike && (
          <button
            onClick={() => onLike(post.id)}
            className="flex items-center gap-2 hover:text-[#800020] transition-colors"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
            </svg>
          </button>
        )}
      </div>

      {isCommenting && (
        <form onSubmit={handleSubmitComment} className="mt-4">
          <textarea
            value={commentContent}
            onChange={(e) => setCommentContent(e.target.value)}
            className="w-full bg-gray-100 border border-gray-200 rounded-[8px] p-3 text-black placeholder-gray-500 focus:ring-1 focus:ring-accent-green focus:border-accent-green"
            rows={2}
            placeholder="Write a comment..."
          />
          <div className="mt-2 flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setIsCommenting(false)}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-black text-white rounded-[8px] hover:bg-gray-800 transition-colors text-sm"
              disabled={!commentContent.trim()}
            >
              Comment
            </button>
          </div>
        </form>
      )}

      {post.comments?.length > 0 && (
        <div className="mt-4 space-y-4">
          {Array.isArray(post.comments) && post.comments.map((comment) => (
            <div key={comment.id} className="flex items-start space-x-3 bg-gray-50 p-3 rounded-lg">
              <img
                src={getAvatarUrl(comment.user_profiles)}
                alt={`${comment.user_profiles?.first_name || 'User'} ${comment.user_profiles?.last_name || ''}`}
                className="w-8 h-8 rounded-full object-cover"
                onError={(e) => {
                  const target = e.target;
                  const name = comment.user_profiles ? getDisplayName(comment.user_profiles) : 'Anonymous';
                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`;
                }}
              />
              <div>
                <div className="flex items-baseline space-x-2">
                  <h4 className="font-medium text-sm text-black">
                    <div className="flex items-center gap-2"> 
                      <span>{getDisplayName(comment.user_profiles)}</span>
                      {comment.user_profiles?.is_admin && (
                        <span className="px-2 py-0.5 bg-black text-white text-xs rounded-full">
                          Admin
                        </span>
                      )}
                    </div>
                  </h4>
                  <span className="text-xs text-gray-500">
                    {formatDate(comment.created_at)}
                  </span>
                </div>
                <p className="text-sm text-black mt-1">{comment.content}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});